import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import Card from "../../../Card";
import ZoneItem from "./ZoneItem";
import ZoneApplicants from "../ZoneApplicants";
import {
  ZONE_APPLICANTS_PORTALS,
  ZONE_APPLICANTS_URL_PARAMS,
} from "../ZoneApplicants/portal";
import EmptyZonesState from "./EmptyZonesState";
import DocumentViewer from "../../../Company/CompanyViewDetails/Documents/DocumentViewer";
import { UIExpandableImage } from "../../../ui";
import messages from "../../../../assets/locale/messages";

import "../_EventDetailFields.scss";

const zoneMapMediaFrameSx = {
  overflow: "hidden",
};

const zoneMapPathExtension = (url) => {
  if (!url || typeof url !== "string") return "";
  const path = url.trim().split("?")[0].split("#")[0];
  const i = path.lastIndexOf(".");
  return i >= 0 ? path.slice(i).toLowerCase() : "";
};

const isZoneMapRasterImageUrl = (url) => {
  const ext = zoneMapPathExtension(url);
  return (
    ext === ".png" ||
    ext === ".jpg" ||
    ext === ".jpeg" ||
    ext === ".gif" ||
    ext === ".webp"
  );
};

const zoneMapFileLabel = (url, fallbackTitle) => {
  if (!url || typeof url !== "string") return fallbackTitle;
  const segment = url.trim().split("/").pop();
  return (segment && segment.split("?")[0]) || fallbackTitle;
};

const EventZoneMap = ({ url, titleLabel, lang }) => {
  const { registrationReq, shared } = messages[lang];
  const showAsImage = isZoneMapRasterImageUrl(url);
  const doc = {
    url,
    name: titleLabel,
    fileName: zoneMapFileLabel(url, titleLabel),
    pageCount: 1,
  };

  return (
    <Card
      title={titleLabel}
      classNames={{ body: "event-detail-card-body--flush" }}
    >
      {showAsImage ? (
        <UIExpandableImage
          src={url}
          alt={titleLabel}
          frameSx={zoneMapMediaFrameSx}
          thumbnailImgSx={{
            width: "100%",
            height: 520,
            objectFit: "cover",
          }}
          expandAriaLabel={shared.expandImageFullSize}
          closeAriaLabel={shared.closeImageViewer}
        />
      ) : (
        <DocumentViewer
          embedded
          document={doc}
          messagesBundle={registrationReq}
          lang={lang}
        />
      )}
    </Card>
  );
};
EventZoneMap.propTypes = {
  url: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  lang: PropTypes.string.isRequired,
};

const EventZones = ({
  event,
  onAddZone,
  canViewApplicants,
  readOnly,
  portalVariant,
  onRefreshEvent,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events.viewEvents;
  const zoneStep = events.creatEvent.zonesAndLogisticalRequirements;

  const history = useHistory();
  const location = useLocation();

  // The drill-in lives in the URL (not local state) so the browser back button
  // returns to the zone accordions instead of leaving the event view.
  const selectedZoneId =
    new URLSearchParams(location.search).get(ZONE_APPLICANTS_URL_PARAMS.zone) ||
    null;

  const goToZone = (zoneId) => {
    const params = new URLSearchParams(location.search);
    params.set(ZONE_APPLICANTS_URL_PARAMS.zone, String(zoneId));
    history.push({ pathname: location.pathname, search: params.toString() });
  };

  const goBackToZones = () => {
    const params = new URLSearchParams(location.search);
    params.delete(ZONE_APPLICANTS_URL_PARAMS.zone);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  // The applicants list replaces the zone accordions in place — it is a drill-in
  // of the Zones tab, not a separate page.
  if (selectedZoneId != null) {
    return (
      <ZoneApplicants
        event={event}
        zoneId={selectedZoneId}
        portalVariant={portalVariant}
        readOnly={readOnly}
        onRefreshEvent={onRefreshEvent}
        onBack={goBackToZones}
      />
    );
  }

  const zones = event?.zones ?? [];
  const zoneMapUrl =
    typeof event?.zones_image_url === "string"
      ? event.zones_image_url.trim()
      : "";
  const hasZoneMap = Boolean(zoneMapUrl);

  if (!zones.length && !hasZoneMap) {
    return (
      <EmptyZonesState
        title={t.emptyZonesTitle}
        description={t.emptyZonesDescription}
        actionLabel={t.addNewZone}
        onAddZone={onAddZone}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {zones.map((zone, idx) => (
        <ZoneItem
          key={zone?.id ?? idx}
          zone={zone}
          index={idx}
          defaultExpanded={idx === 0}
          onViewApplicants={
            canViewApplicants && zone?.id
              ? () => goToZone(zone.id)
              : undefined
          }
        />
      ))}
      {hasZoneMap && (
        <EventZoneMap
          url={zoneMapUrl}
          titleLabel={zoneStep.zoneMap}
          lang={lang}
        />
      )}
    </Box>
  );
};

EventZones.propTypes = {
  event: PropTypes.object,
  onAddZone: PropTypes.func,
  canViewApplicants: PropTypes.bool,
  /** Hides every staffing action inside the applicants drill-in. */
  readOnly: PropTypes.bool,
  portalVariant: PropTypes.oneOf(Object.values(ZONE_APPLICANTS_PORTALS)),
  onRefreshEvent: PropTypes.func,
};

EventZones.defaultProps = {
  event: null,
  onAddZone: undefined,
  canViewApplicants: false,
  readOnly: false,
  portalVariant: ZONE_APPLICANTS_PORTALS.company,
  onRefreshEvent: undefined,
};

export default EventZones;
